import Address from '../../models/Address';
import AIChatMessage, { AIChatResponse } from '../../models/AIChatMessage';
import type { VeraGoogleSearchGrounding } from '../../models/VeraGoogleSearchGrounding';
import Contact from '../../models/Contact';
import ContactDetails from '../../models/ContactDetails';
import FundOpportunity from '../../models/FundOpportunity';
import FundOpportunityInvestment from '../../models/FundOpportunityInvestment';
import {
  InvestmentAllocation,
  InvestmentEntity,
  InvestorAllocationsResponse,
} from '../../models/InvestmentEntity';
import InvestmentEntityContact from '../../models/InvestmentEntityContact';
import { IRRData } from '../../models/IRRDataPoint';
import { IRRUpdate } from '../../models/IRRUpdate';
import { BaseBackendServiceAssets } from './BaseBackendServiceAssets';

export class BaseBackendServiceFundDomain extends BaseBackendServiceAssets {
  getUnconvertedLeadsForContact(): Promise<FundOpportunityInvestment[]> {
    return this.authApiGetAuthed(
      'UnconvertedLeads',
      'Network error while fetching unconverted leads',
      (json) => json.data as FundOpportunityInvestment[],
    );
  }

  getFundOpportunity(id: string): Promise<{ data: FundOpportunity; content: string | null }> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL(`fundOpportunities/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const responseData = await response.json();
      if (responseData.data.addresses) {
        responseData.data.addresses = responseData.data.addresses.map(
          (address: any): Address => ({
            ...address,
            latitude: parseFloat(address.latitude),
            longitude: parseFloat(address.longitude),
          }),
        );
      }
      return { data: responseData.data, content: null };
    });
  }

  /**
   * The stable playback URL for a READY library video (GET
   * /videos/:externalId, checkLoginAnyRole on the backend). Cacheable by
   * Cloud CDN once provisioned; falls back to a short-lived GCS signed URL
   * otherwise — same call either way, the backend decides. Used by
   * PropertyCardVideoEmbed for any card whose video source is 'library'.
   */
  getVideoPlaybackUrl(
    externalId: string,
  ): Promise<{ url: string; title?: string; description?: string }> {
    return this.authApiGetAuthed(
      `videos/${externalId}?returnUrl=true`,
      'Unable to load video playback URL',
      (json) =>
        ({ url: json.url, title: json.title, description: json.description }) as {
          url: string;
          title?: string;
          description?: string;
        },
    );
  }

  getLatestIRRUpdatesByLabel(fundOpportunityId: string): Promise<IRRUpdate[]> {
    return this.getIRRUpdates(fundOpportunityId).then((updates) => {
      const byLabel: { [key: string]: IRRUpdate } = {};
      updates.forEach((update) => {
        byLabel[update.label] = update;
      });
      return Object.values(byLabel);
    });
  }

  getIRRUpdates(fundOpportunityId: string): Promise<IRRUpdate[]> {
    return this.authApiGetAuthed(
      `opportunities/${fundOpportunityId}/irrUpdates`,
      'Network error while fetching IRR updates',
      (json) => json as IRRUpdate[],
    );
  }

  getUserNameAndInitials(): Promise<
    ContactDetails & {
      impersonating: boolean;
      impersonationExpires: number;
      impersonationCaptureLogs: boolean;
      actorSub: string | null;
      targetSub: string | null;
    }
  > {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL('userNameAndInitials'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const impersonatingHeader = response.headers.get('x-impersonation-context');
      const rawExpires = response.headers.get('x-impersonation-expiry');
      const actorSub = response.headers.get('x-impersonation-actor-sub');
      const targetSub = response.headers.get('x-impersonation-target-sub');
      let impersonating = false;
      let impersonationExpires = 0;
      let impersonationCaptureLogs = false;
      if (impersonatingHeader) {
        const context = JSON.parse(impersonatingHeader);
        impersonating = context['impersonating'];
        impersonationCaptureLogs = context['captureLogs'] === true;
        impersonationExpires = rawExpires ? parseInt(rawExpires) : 0;
      }
      const data = await response.json();
      return {
        ...data.data,
        impersonating,
        impersonationExpires,
        impersonationCaptureLogs,
        actorSub,
        targetSub,
      };
    });
  }

  getIRRUpdatesForMultiple(externalIds: string[]): Promise<IRRUpdate[]> {
    if (!externalIds || externalIds.length === 0) {
      return Promise.resolve([]);
    }
    return this.authApiMutateAuthed(
      'opportunities/irrUpdates',
      'POST',
      { ids: externalIds },
      (json) => json as IRRUpdate[],
    );
  }

  getParsedIRRDataForLabel(fundOpportunityId: string | number, month: string): Promise<IRRData> {
    return this.setupAssetStorageWithRetry(false)
      .then((tokenData) =>
        this.getAsset(`irrData/${fundOpportunityId}/${month}.json`, tokenData.token.token, {
          url: false,
          listItems: false,
        }),
      )
      .then((raw) => (JSON.parse(raw as string) as { data: IRRData }).data);
  }

  getAIChatResponse(
    fundOpportunityId: string,
    message: string,
    history: AIChatMessage[],
    id?: string,
  ): Promise<AIChatResponse> {
    return this.authApiMutateAuthed(
      `fundOpportunities/${fundOpportunityId}/chat`,
      'POST',
      { message, history, id },
      (data) =>
        ({
          id: data.id,
          ...(data.data as Partial<AIChatResponse>),
          timestamp: new Date(),
        }) as AIChatResponse,
    );
  }

  sendVeraGlobalMessage(
    message: string,
    history: AIChatMessage[],
    chatId?: string,
  ): Promise<{
    reply: string;
    chatId: string;
    googleSearchGrounding?: VeraGoogleSearchGrounding | null;
  }> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL('vera/global-chat'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(
          chatId
            ? { message, chatId, clientDate: new Date().toISOString() }
            : { message, history, chatId, clientDate: new Date().toISOString() },
        ),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Error: ${response.status} - ${text}`);
      }
      const data = await response.json();
      const payload = data?.data || data;
      return {
        reply: payload.reply,
        chatId: payload.chatId,
        googleSearchGrounding: payload.googleSearchGrounding ?? null,
      };
    });
  }

  addContactToEntity(
    entityId: string,
    contactId: string,
    role: string = 'OWNER',
    representativeType?: string,
    otherRepresentativeType?: string,
  ): Promise<void> {
    const body: Record<string, string> = { role };
    if (representativeType) body.representativeType = representativeType;
    if (otherRepresentativeType) body.otherRepresentativeType = otherRepresentativeType;
    return this.authApiMutateAuthed<void>(
      `investment-entities/${entityId}/contacts/${contactId}`,
      'POST',
      body,
      () => undefined,
    );
  }

  getEntityContacts(entityId: string): Promise<InvestmentEntityContact[]> {
    return this.authApiGetAuthed(
      `investment-entities/${entityId}/contacts`,
      'Unable to retrieve entity contacts',
      (json) => json.data || [],
    );
  }

  searchContactsForEntity(searchTerm: string, entityId?: string): Promise<Contact[]> {
    const query = entityId
      ? `contacts/search?term=${encodeURIComponent(searchTerm)}&excludeEntityId=${encodeURIComponent(entityId)}`
      : `contacts/search?term=${encodeURIComponent(searchTerm)}`;
    return this.authApiGetAuthed(query, 'Unable to search contacts', (json) => json.data || []);
  }

  getInvestmentEntity(investmentEntityId: string): Promise<InvestmentEntity> {
    return this.authApiGetAuthed(
      `investment-entities/${investmentEntityId}`,
      'Unable to retrieve investment entity',
      (json) => json.data,
    );
  }

  getEntityAllocations(entityId: string): Promise<InvestmentAllocation[]> {
    return this.authApiGetAuthed(
      `investment-entities/${entityId}/allocations`,
      'Unable to fetch entity allocations',
      (json) => {
        console.log('Backend service allocations response:', json);
        let allocations = [];
        if (json.data && json.data.allocations) {
          allocations = json.data.allocations;
        } else if (json.allocations) {
          allocations = json.allocations;
        } else if (Array.isArray(json.data)) {
          allocations = json.data;
        }
        console.log('Extracted allocations:', allocations);
        return allocations;
      },
    );
  }

  promoteContactToOwner(entityId: string, contactId: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `investment-entities/${entityId}/contacts/${contactId}/promoteToOwner`,
      'POST',
      undefined,
      () => undefined,
    );
  }

  removeContactFromEntity(entityId: string, contactId: string): Promise<void> {
    return this.authApiDeleteVoid(`investment-entities/${entityId}/contacts/${contactId}`);
  }

  associateInvestorWithEntity(investorId: string, investmentEntityId: string): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `investors/${investorId}/investment-entity`,
      'PATCH',
      { investmentEntityId },
      () => undefined,
    );
  }

  associateInvestorWithEntityAndAmount(
    investorId: string,
    investmentEntityId: string,
    amount: number,
  ): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `investors/${investorId}/allocations`,
      'POST',
      { investmentEntityId, amount },
      () => undefined,
    );
  }

  getInvestorEntityAllocations(investorId: string): Promise<InvestorAllocationsResponse> {
    return this.authApiGetAuthed(
      `investors/${investorId}/allocations`,
      'Unable to fetch entity allocations',
      (json) => json.data || json,
    );
  }

  updateInvestorAllocations(
    investorId: string,
    allocations: { investmentEntityId: string; amount: number }[],
  ): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `investors/${investorId}/allocations`,
      'PUT',
      { allocations },
      () => undefined,
    );
  }

  redistributeInvestorEntityAllocations(
    investorId: string,
    allocations: { investmentEntityId: string; allocatedAmount: number }[],
  ): Promise<void> {
    return this.authApiMutateAuthed<void>(
      `investors/${investorId}/entity-allocations/redistribute`,
      'POST',
      { allocations },
      () => undefined,
    );
  }

  createPlaceholderContact(
    entityId: string,
    contactData: {
      firstName: string;
      lastName: string;
      email: string;
      role?: string;
      representativeType?: string;
      otherRepresentativeType?: string;
    },
  ): Promise<Contact> {
    return this.authApiMutateAuthed(
      `investment-entities/${entityId}/placeholder-contacts`,
      'POST',
      contactData,
      (json) => json.data as Contact,
    );
  }
}
